import { useRef, useState } from "react";
import Layout from "../components/Layout";
import CountdownTimer from "../components/CountdownTimer";
import ShareButton from "../components/ShareButton";
import DownloadImageButton from "../components/DownloadImageButton";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Play, RotateCcw, Type, ChevronDown, ChevronUp } from "lucide-react";

const COLOR = "#6366F1";
const DURATION = 60;

const Skeleton = () => (
  <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-10 animate-pulse text-center space-y-6">
    <div className="h-16 w-2/3 bg-[#e5e1d5] rounded mx-auto" />
    <div className="h-6 w-1/2 bg-[#e5e1d5] rounded mx-auto" />
  </div>
);

const TipografiaTorturada = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [showRefs, setShowRefs] = useState(false);
  const cardRef = useRef(null);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/generate/tipografia-torturada", {});
      setData(res.data);
      setPhase("ready");
      setShowRefs(false);
    } catch {
      toast.error("Ups, algo salió mal. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const start = () => setPhase("running");
  const finish = () => setPhase("done");

  const d = data?.data || {};

  return (
    <Layout accent={COLOR}>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        <div className="mb-8">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#6366F1] text-white border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-4">
            tool · 07 · nuevo
          </span>
          <h1 className="font-display font-black text-5xl sm:text-7xl leading-[0.95] tracking-tight mb-4">
            tipografía <span className="text-[#6366F1]">torturada</span>.
          </h1>
          <p className="font-mono text-sm sm:text-base text-[#4a4a4a] max-w-xl">
            Una palabra + el estilo más contradictorio posible. Lettering a mano en 60 segundos.
          </p>
        </div>

        {phase === "idle" && !loading && (
          <button
            data-testid="tipografia-generate-btn"
            onClick={generate}
            className="press bg-[#6366F1] text-white border-4 border-[#1a1a1a] rounded-2xl px-8 py-5 font-display font-black text-2xl shadow-hard-md inline-flex items-center gap-2"
          >
            <Type className="w-6 h-6" strokeWidth={3} />
            ¡Torturar tipografía!
          </button>
        )}

        {loading && phase === "idle" && <Skeleton />}

        {phase === "ready" && data && (
          <>
            <div ref={cardRef} className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in relative text-center" data-testid="tipografia-ready">
              <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">
                guango · tipografía torturada
              </div>
              <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-5 text-[#737373]">la palabra</div>
              <div
                data-testid="palabra"
                className="font-display font-black text-[#1a1a1a] tracking-tight leading-none mb-10 break-words"
                style={{ fontSize: "clamp(3.5rem, 13vw, 8rem)" }}
              >
                {d.palabra}
              </div>

              <div className="border-t-4 border-[#1a1a1a] pt-6 max-w-lg mx-auto">
                <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-3 text-[#737373]">debes escribirla en:</div>
                <div className="font-display font-bold text-xl sm:text-2xl text-[#6366F1] mb-3">{d.estilo}</div>
                {d.contradiccion && (
                  <p className="font-mono italic text-xs sm:text-sm text-[#737373]">{d.contradiccion}</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-6" data-exclude-from-image="true">
              <button
                data-testid="tipografia-start-btn"
                onClick={start}
                className="press inline-flex items-center gap-2 bg-[#22c55e] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <Play className="w-5 h-5" strokeWidth={3} /> iniciar 60s
              </button>
              <button
                data-testid="tipografia-regen-btn"
                onClick={generate}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                otra palabra
              </button>
              <ShareButton promptId={data.id} color={COLOR} testId="tipografia-share" />
              <DownloadImageButton
                targetRef={cardRef}
                fileName={`guango-tipografia-torturada-${data.id.slice(0, 8)}.png`}
                color={COLOR}
                testId="tipografia-download"
              />
            </div>

            {Array.isArray(d.referencias) && d.referencias.length > 0 && (
              <div className="mt-6">
                <button
                  data-testid="tipografia-toggle-refs"
                  onClick={() => setShowRefs((s) => !s)}
                  className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
                >
                  {showRefs ? <ChevronUp className="w-4 h-4" strokeWidth={3} /> : <ChevronDown className="w-4 h-4" strokeWidth={3} />}
                  {showRefs ? "ocultar referencia" : "ver referencia de estilo"}
                </button>
                {showRefs && (
                  <ul data-testid="tipografia-refs" className="mt-4 bg-[#fff7e6] border-4 border-[#1a1a1a] rounded-2xl p-5 space-y-2">
                    {d.referencias.map((r, i) => (
                      <li key={i} className="font-mono text-sm flex items-start gap-3">
                        <span className="mt-1 w-3 h-3 rounded-full border-2 border-[#1a1a1a] shrink-0 bg-[#6366F1]" />
                        {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}

        {phase === "running" && (
          <div data-testid="tipografia-running">
            <div className="bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-display font-black text-2xl mb-6">
              {d.palabra} · <span className="text-[#6366F1]">{d.estilo}</span>
            </div>
            <CountdownTimer duration={DURATION} color={COLOR} running format="seconds" warnAt={10} onComplete={finish} />
          </div>
        )}

        {phase === "done" && (
          <div className="bg-[#6366F1] text-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in" data-testid="tipografia-done">
            <div className="font-display font-black text-5xl sm:text-7xl mb-4">¡tiempo! 📝</div>
            <p className="font-mono text-base sm:text-lg mb-8">¿Cómo quedó tu lettering?</p>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid="tipografia-another-btn"
                onClick={generate}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <RotateCcw className="w-5 h-5" strokeWidth={3} /> otra palabra
              </button>
              {data && <ShareButton promptId={data.id} color="#fff" testId="tipografia-done-share" />}
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default TipografiaTorturada;
